import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Dropdown from '../../../../components/Dropdown';
import Toast from '../../../../components/Toast';
import CustomizedSwitch from '../../../../components/Switch';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import PlanService from '../../../../services/plan.service';
import ProductService from '../../../../services/product.service';
import MasterService from '../../../../services/master.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';

interface PlanRow {
  id: number;
  plan_code: string;
  plan_name: string;
  product_code: string | null;
  billing_cycle_name: string | null;
  price: string;
  currency_code: string | null;
  currency_symbol: string | null;
  status_name: string | null;
  status_colour: string | null;
}

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface StatusOption {
  id: number;
  status_name: string;
  colour_code: string;
}

const PlanList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'plan_code' });
  const [items, setItems] = useState<PlanRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<PlanRow | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    MasterService.list({ type: 'subscriptionStatuses', includeDeleted: false, limit: 100 }).then((response: any) => {
      setStatuses(response?.data?.items ?? []);
    });
  }, []);

  const { loading } = useFetch({
    service: PlanService,
    func: 'list',
    payload: {
      includeDeleted: showInactive,
      page,
      limit: perPage,
      productId: productFilter ? Number(productFilter) : undefined,
      statusId: statusFilter ? Number(statusFilter) : undefined,
    },
    dependency: [showInactive, page, perPage, productFilter, statusFilter, refreshToken],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.masters}${AuthRoutes.plans}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const response: any = await PlanService.remove(deleteTarget.id);
      const softOrHard = response?.data?.softOrHard;
      toast.success(
        <Toast
          message={
            softOrHard === 'soft'
              ? t('masters.plans.list.pageTexts.deleteSuccessSoft')
              : t('masters.plans.list.pageTexts.deleteSuccessHard')
          }
        />,
        { icon: false }
      );
      setRefreshToken((v) => v + 1);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, t]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'plan_code', sortEnabled: true, width: '12%', label: t('masters.plans.list.pageTexts.table.columns.code') },
      { id: 'plan_name', sortEnabled: true, width: '18%', label: t('masters.plans.list.pageTexts.table.columns.name') },
      { id: 'product_code', sortEnabled: true, width: '13%', label: t('masters.plans.list.pageTexts.table.columns.product') },
      { id: 'billing_cycle_name', sortEnabled: true, width: '15%', label: t('masters.plans.list.pageTexts.table.columns.billingCycle') },
      { id: 'price', sortEnabled: true, width: '12%', label: t('masters.plans.list.pageTexts.table.columns.price') },
      { id: 'status', sortEnabled: true, width: '15%', label: t('masters.plans.list.pageTexts.table.columns.status') },
      { id: 'actions', sortEnabled: false, width: '15%', label: t('masters.plans.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: PlanRow; className?: string }) => (
      <tr className={className}>
        <td>{item.plan_code}</td>
        <td>{item.plan_name}</td>
        <td>{item.product_code ?? '—'}</td>
        <td>{item.billing_cycle_name ?? '—'}</td>
        <td>
          {item.currency_symbol ?? ''}
          {item.price}
        </td>
        <td>
          {item.status_name && item.status_colour ? (
            <StatusBadge label={item.status_name} colour={item.status_colour} />
          ) : (
            '—'
          )}
        </td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => handleEdit(item.id),
              },
              {
                id: 'delete',
                label: t('common.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: () => setDeleteTarget(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [handleEdit]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('masters.plans.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('masters.plans.list.pageTexts.title')}</h4>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('masters.plans.list.pageTexts.showInactive')}
            name="showInactive"
            checked={showInactive}
            onChange={() => setShowInactive((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('masters.plans.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.plans}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-3">
          <Dropdown
            name="productFilter"
            value={productFilter}
            placeholder={t('masters.plans.list.pageTexts.filters.allProducts')}
            options={products.map((p) => ({ id: String(p.id), name: p.product_code }))}
            noSearch
            onChange={(e) => setProductFilter(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <Dropdown
            name="statusFilter"
            value={statusFilter}
            placeholder={t('masters.plans.list.pageTexts.filters.allStatuses')}
            options={statuses.map((s) => ({ id: String(s.id), name: s.status_name }))}
            noSearch
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('masters.plans.list.pageTexts.deleteDialog.title')}
        content={t('masters.plans.list.pageTexts.deleteDialog.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('masters.plans.list.pageTexts.deleteDialog.cancel')}
              onClick={() => setDeleteTarget(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('masters.plans.list.pageTexts.deleteDialog.confirm')}
              onClick={handleDeleteConfirm}
            />
          </>
        }
      />
    </>
  );
};

export default PlanList;
